import React, {useState, useRef, useEffect} from 'react';
import ModalBody, {Modal, ModalContent, ModalFooter} from "../../components/Modal";
import {FormDefinition} from "../../lib/forms";
import {useForm} from "../../lib/formHook";
import {ModelFormField} from "../../components/ModelFormHOC";
import Form from "../../components/Form";
import {useEstados, useGetCidades} from "../../lib/DataHooks/base";
import ActionButton from "../../components/ActionButton";
import {actions} from "../../lib/app";
import {useAppDispatch} from "../../lib/hooks";
import {post} from "../../utils/frRequest";
import ReCAPTCHA from "react-google-recaptcha";
import {googleRecaptchaSiteKey} from "../../config";
import {SwitchTransition, CSSTransition} from "react-transition-group";
import {IonButton} from "@ionic/react";

const formDef: FormDefinition = {
  nome: {name: 'nome', label: 'Nome', type: "string", required: true},
  telefone: {name: 'telefone', label: 'Telefone', type: "telefone", required: true},
  email: {name: 'email', label: 'Email', type: "email", required: true},
  estado: {name: 'estado', label: 'Estado', type: "searchInput", required: true},
  cidade: {name: 'cidade', label: 'Cidade', type: "searchInput", required: true},
}

// 2 paragraphs lorem ipsum text
const introText =
  "Preencha o cadastro abaixo para solicitar o acesso ao Virbac no Campo. " +
  "Seu cadastro será analisado e se aprovado você receberá as instruções de acesso no email informado. ";

export interface NovoCadastroValues {
  nome: string;
  telefone: string;
  email: string;
  estado: string;
  cidade: string;
}

interface NovoCadastroProps {
  isOpen: boolean;
  onDidDismiss: () => void;
}

type Step = 'form' | 'finished';

const NovoCadastro: React.FC<NovoCadastroProps> = (props) => {
  const {isOpen, onDidDismiss} = props;
  const [step, setStep] = useState<Step>('form');
  const [values, setValues] = useState<NovoCadastroValues>();

  useEffect(() => {
    if (isOpen) {
      setStep('form');
    }
  }, [isOpen]);

  const onCompleteCadastro = (_values: NovoCadastroValues) => {
    setValues(_values);
    setStep('finished');
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onDidDismiss={onDidDismiss}
        title="Novo Cadastro"
        cancelLabel="Fechar"
      >
        <SwitchTransition>
          <CSSTransition
            key={step}
            classNames="transition-slide-l"
            timeout={250}
          >
            <ModalBody>
              {step === 'form' &&
                <CadastroForm
                  onComplete={onCompleteCadastro}
                />
              }
              {step === 'finished' &&
                <CadastroFinished
                  values={values!}
                  onDidDismiss={onDidDismiss}
                />
              }
            </ModalBody>
          </CSSTransition>
        </SwitchTransition>
      </Modal>
    </>
  );
}

export default NovoCadastro;


interface CadastroFormProps {
  onComplete: (values: NovoCadastroValues) => void;
}

const CadastroForm = (props: CadastroFormProps) => {
  const {onComplete} = props;
  const form = useForm(formDef);
  const recaptchaRef = useRef<any>();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const {estado} = form.values;
  const [estados, loadingEstados] = useEstados();
  const [cidades, loadingCidades] = useGetCidades({estado}, {
    enabled: !!estado,
  });

  const onChangeEstado = () => {
    form.setFieldValue('cidade', '', false);
  }

  const onSubmit = async () => {
    let token;
    try {
      token = await recaptchaRef.current.executeAsync();
    } catch (error) {
      console.error(error);
    }
    if (!token) {
      dispatch(actions.notifyError('Ocorreu um erro inesperado ao validar se você não é um robô.'));
      return;
    }

    const {success, values, messagesFlat} = form.validate();
    if (!success) {
      dispatch(actions.notifyError(messagesFlat.join('<br/>')));
      return;
    }

    setIsLoading(true);
    try {
      const res = await post('/veApi/novoCadastro',
        {...values, token}, {skipAuthToken: true});

      const {success, error} = res;
      if (!success) {
        dispatch(actions.notifyError(error));
        return;
      }
      onComplete(values);
    } catch (error: any) {
      dispatch(actions.notifyError(error.message));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      <div className="mb-4">
        {introText}
      </div>

      <ModalContent>
        <Form form={form}>
          <ModelFormField field="nome" disabled={isLoading}/>
          <ModelFormField field="telefone" disabled={isLoading}/>
          <ModelFormField field="email" disabled={isLoading}/>
          <ModelFormField
            field="estado"
            options={estados}
            optionLabel="uf"
            optionValue="uf"
            loading={loadingEstados}
            onChange={onChangeEstado}
            disabled={isLoading}
          />

          <ModelFormField
            field="cidade"
            options={cidades}
            optionLabel="nome"
            optionValue="nome"
            loading={loadingCidades}
            disabled={!estado || isLoading}
            interfaceOptions="app-select-alert wide"
          />
        </Form>

        <div className="mt-8 flex justify-center">
          <ReCAPTCHA
            ref={recaptchaRef}
            size="invisible"
            sitekey={googleRecaptchaSiteKey ?? ''}
            badge="inline"
          />
        </div>
      </ModalContent>

      <ModalFooter>
        <div className="flex items-center gap-4 justify-between">
          <ActionButton
            loading={isLoading}
            onClick={onSubmit}
          >
            Enviar
          </ActionButton>
        </div>
      </ModalFooter>
    </>
  )
}


interface CadastroFinishedProps {
  values: NovoCadastroValues;
  onDidDismiss: () => void;
}

const CadastroFinished = ({values, onDidDismiss}: CadastroFinishedProps) => {
  const {email} = values;

  return (
    <>
      <ModalContent>
        <div className="flex flex-col gap-4">
          <div className="text-lg font-bold text-center">
            Cadastro realizado com sucesso!
          </div>
          <div>
            Email de contato: <b>{email}</b>.
          </div>
          <div>
            Você receberá instruções de acesso assim que seu cadastro for verificado.
          </div>
        </div>
      </ModalContent>

      <ModalFooter>
        <div className="flex items-center gap-4 justify-between">
          <IonButton
            onClick={onDidDismiss}
          >
            Fechar
          </IonButton>
        </div>
      </ModalFooter>
    </>
  )
}